import { TFunction } from "i18next";
import * as Yup from "yup";

import { Claim } from "../../../api/ClientFileApi";
import { hasEntryNewerThan, newestBalanceEntryOf } from "../../utils/claimBalance";
import { processableRowsOf } from "../../utils/claimSelection";
import { SurchargeRow, SurchargeScope } from "./BalanceSurchargeDialog.types";

/** Decimal places the percentage field accepts. Two is the agreed depth; the backend caps nothing. */
export const PERCENTAGE_MAX_DECIMALS = 2;

/**
 * A number as integer units at the scale of its own decimal digits — 4,25 becomes 425 at scale 2 —
 * read off its string form so the arithmetic below is exact whatever the user typed. Handles the
 * exponent notation `String()` falls back to for very small values.
 */
function toUnits(value: number): { units: bigint; scale: number } {
  const [mantissa = "0", exponent = "0"] = value.toString().toLowerCase().split("e");
  const [whole = "0", fraction = ""] = mantissa.split(".");
  const scale = fraction.length - Number(exponent);
  const digits = BigInt(whole + fraction);

  return scale < 0 ? { units: digits * 10n ** BigInt(-scale), scale: 0 } : { units: digits, scale };
}

/**
 * One claim's balance raised by `percentage` percent, rounded to cents — the frontend half of the
 * backend's ClaimBalance.ApplySurcharge. Rounded per claim and only then summed, so the preview
 * totals match what the backend writes rather than differing by a cent per claim.
 *
 * Done in integer cents rather than with `Math.round` on the product: binary floating point decides
 * a half cent differently from the decimal arithmetic the backend rounds away from zero, and on
 * ordinary input it does so in the opposite direction — 1,00 € at 0,5 % is 1,005, which the backend
 * writes as 1,01 while `Math.round` yields 1,00.
 */
export function raiseByPercentage(saldo: number, percentage: number): number {
  const cents = BigInt(Math.round(saldo * 100));
  const { units, scale } = toUnits(percentage);
  // 100 % in the percentage's own units, so `whole + units` is the growth factor as an integer.
  const whole = 100n * 10n ** BigInt(scale);
  const raised = cents * (whole + units);
  const magnitude = raised < 0n ? -raised : raised;
  const rounded = (magnitude + whole / 2n) / whole;

  return Number(raised < 0n ? -rounded : rounded) / 100;
}

/**
 * The selected claims as the overlay lists them, in the order the list shows them. A claim without a
 * history has no balance to raise — a surcharge would only file a 0,00 entry to be deleted by hand —
 * so it is listed as skipped rather than hidden, and the overlay accounts for every row the user
 * ticked. The server leaves such a claim out again for the request it receives; this is what the
 * user is shown before confirming.
 */
export function surchargeRowsOf(
  claims: Claim[],
  selectedClaimIds: ReadonlySet<string>,
  date: Date | null,
  percentage: number
): SurchargeRow[] {
  return claims
    .filter((claim) => selectedClaimIds.has(claim.id))
    .map((claim) => {
      const keepsSaldo = date !== null && hasEntryNewerThan(claim, date);

      return {
        claim,
        keepsSaldo,
        newSaldo: keepsSaldo ? claim.currentSaldo : raiseByPercentage(claim.currentSaldo, percentage),
        skipReason: newestBalanceEntryOf(claim) === null ? "noHistory" : null,
      };
    });
}

export function surchargeScopeOf(rows: SurchargeRow[]): SurchargeScope {
  const processable = processableRowsOf(rows);

  return {
    affectedCount: processable.length,
    currentTotal: processable.reduce((sum, row) => sum + row.claim.currentSaldo, 0),
    unchangedCount: processable.filter((row) => row.keepsSaldo).length,
  };
}

/**
 * The total the affected claims will show once the surcharge is applied, each claim rounded to cents
 * first. A claim that keeps its balance contributes the current value, so the preview never promises
 * a total the list will not show. The backend reports its NewTotal the same way.
 */
export function projectedTotalOf(rows: SurchargeRow[]): number {
  return processableRowsOf(rows).reduce((sum, row) => sum + row.newSaldo, 0);
}

export function balanceSurchargeValidationSchema(t: TFunction) {
  return Yup.object().shape({
    // Empty, zero and negative all get the same message — all three mean the same thing to the user.
    // No upper bound: a surcharge of several hundred percent is a legitimate long-range projection.
    percentage: Yup.number()
      .nullable()
      .required(t("claims.surcharge.validation.percentageRequired"))
      .moreThan(0, t("claims.surcharge.validation.percentageRequired")),
    // Past and future dates are both allowed, so only an absent one is rejected.
    date: Yup.date()
      .nullable()
      .required(t("common.validation.fieldRequired", { fieldName: t("claims.surcharge.date") })),
    entryType: Yup.string().nullable(),
  });
}
